import React, { useContext } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import { Chip, Box, useTheme, Tooltip, Typography } from '@mui/material';
import {
  isBefore,
  startOfToday,
  differenceInCalendarDays,
  format,
  addMinutes,
  parseISO
} from 'date-fns';
import { AppContext } from '../AppContext';
import { trackFeature } from '../utils/analytics';

const taskEmojiMap = {
  sow: '🌱',
  blackout: '🕶️',
  lights: '💡',
  water: '💧',
  harvest: '✂️',
  delivery: '🚚',
};

const taskColorMap = {
  sow: 'success',
  blackout: 'quaternary',
  lights: 'warning',
  water: 'primary',
  harvest: 'error',
  delivery: 'tertiary',
};

const TaskCalendar = ({ tasks, onTaskClick }) => {
  const theme = useTheme();
  const { userData, farmData } = useContext(AppContext);
  const defaultDateFormat = farmData?.settings?.dateFormat || 'dd-MM-yyyy';
  const weekStart = (farmData?.settings?.calendarWeekStart || 'monday').toLowerCase();
  const calendarStart = weekStart === 'monday' ? 1 : 0;

  const typePriority = {
    delivery: 1,
    harvest:   2,
    lights:    3,
    blackout:  4,
    sow:       5,
    water:     6,
  };

  // Group tasks by date & type
  const groupedTasks = {};
  tasks.forEach(task => {
    const key = `${task.date}_${task.type}`;
    if (!groupedTasks[key]) groupedTasks[key] = [];
    groupedTasks[key].push(task);
  });

  // Build FullCalendar events
  const events = Object.entries(groupedTasks)
    .map(([key, safegroup]) => {
      if (!safegroup.length) return null;
      const [date, type] = key.split('_');
      const representative = safegroup[0];
      return {
        id: key,
        title: `${safegroup.length}x ${representative.label}`,
        start: date,
        end: addMinutes(parseISO(date), 1).toISOString(),
        priority: typePriority[type] ?? 99,
        extendedProps: {
          ...representative,
          customColor: representative.color,
          safegroup,
        },
      };
    })
    .filter(Boolean);

  // Render each grouped event as a Chip
  const renderEventContent = (info) => {
    const { type, label, customColor, emoji: overrideEmoji, safegroup } =
      info.event.extendedProps;
    const shortFmt = defaultDateFormat.replace(/[-/]?yyyy/, '').trim();
    const date = info.event.start;
    const overdue = isBefore(date, startOfToday());
    const isToday = differenceInCalendarDays(date, startOfToday()) === 0;
    const count = safegroup.length > 1 ? ` x${safegroup.length}` : '';
    const text = safegroup
      .map(t => {
        const em = t.emoji || taskEmojiMap[t.type] || '';
        const baseDate = t.type !== 'delivery'
          ? (t.harvestStartDate ? parseISO(t.harvestStartDate) : parseISO(t.date))
          : null;
        const suffix = baseDate
          ? ` (${format(baseDate, shortFmt)})`
          : '';
        return `${em} ${t.label}${suffix}`;
      })
      .join('\n');

    const chipSx = {
      fontSize: '0.8rem',
      padding: '2px 8px',
      height: 'auto',
      borderRadius: '12px',
      '& .MuiChip-label': {
        padding: '3px 0',
        whiteSpace: 'pre-line',
        textAlign: 'left',
      },
      ...(overdue
        ? { border: `2px solid ${theme.palette.error.main}` }
        : isToday
          ? { border: `2px solid ${theme.palette.secondary.main}` }
          : {}),
    };

    const mappedColor = taskColorMap[type];
    const baseBgColor = type === 'custom'
      ? (customColor || theme.palette.grey[700])
      : (
        type === 'lights'
          ? '#d37e3d'
          : type === 'blackout'
            ? (theme.palette[mappedColor]?.light || theme.palette[mappedColor]?.main || theme.palette.grey[700])
            : (theme.palette[mappedColor]?.dark || theme.palette[mappedColor]?.main || theme.palette.grey[700])
      );

    return (
      <Box sx={{ p: 0.5 }}>
        <Tooltip
          title={
            <Typography sx={{ fontSize: '0.8rem' }}>
              Type: {type.charAt(0).toUpperCase() + type.slice(1)}{count}
            </Typography>
          }
          arrow
          placement="top"
        >
          <Chip
            size="small"
            label={text}
            variant="filled"
            sx={{
              backgroundColor: baseBgColor,
              color: theme.palette.getContrastText(baseBgColor),
              ...chipSx,
            }}
          />
        </Tooltip>
      </Box>
    );
  };

  // Handle clicks on a Chip (or group of Chips)
  const handleEventClick = (clickInfo) => {
    const props = clickInfo.event.extendedProps || {};
    const safegroup = Array.isArray(props.safegroup) ? props.safegroup : [];
    if (!safegroup.length) return;

    // 🔥 Analytics: who clicked which tasks on the calendar?
    trackFeature('task_calendar_event_click', {
      count: safegroup.length,
      taskIds: safegroup.map(t => t.id),
      types: Array.from(new Set(safegroup.map(t => t.type))),
    });

    // Delegate to parent (opens confirm dialog)
    onTaskClick?.(safegroup);
  };

  return (
<Box
  sx={{
    /* Base */
    '& .fc': {
      fontFamily: theme.typography.fontFamily,
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
    },

    /* Toolbar base */
    '& .fc-toolbar': {
      backgroundColor: theme.palette.primary.paper,
      padding: '3px 0',
      display: 'flex',
      flexWrap: 'nowrap',              // desktop default
      alignItems: 'center',
      gap: 0,
    },

    '& .fc-toolbar-title': {
      fontSize: { xs: '1.1rem', sm: '1.75rem' },
      fontWeight: 500,
      color: theme.palette.primary.text,
      whiteSpace: 'nowrap',
    },

    '& .fc-button': {
      backgroundColor: theme.palette.primary.main,
      border: 'none',
      color: theme.palette.primary.contrastText,
      fontWeight: 500,
      '&:hover': { backgroundColor: theme.palette.primary.dark },
    },
    '& .fc .fc-button.fc-button-active:focus': { outline: 'none !important', boxShadow: 'none !important' },
    '& .fc .fc-button.fc-button-active': { backgroundColor: theme.palette.primary.dark, color: theme.palette.primary.contrastText },
    '& .fc-button:focus, & .fc-button:active, & .fc-button:focus-visible': {
      outline: 'none !important',
      boxShadow: 'none !important',
      backgroundColor: `${theme.palette.primary.dark} !important`,
    },

    /* Header cells */
    '& .fc-col-header-cell': {
      border: `1px solid ${theme.palette.primary.dark}`,
      backgroundColor: theme.palette.primary.main,
    },
    '& .fc-col-header-cell-cushion': { color: theme.palette.primary.contrastText, fontWeight: 600 },

    /* Day numbers */
    '& .fc-daygrid-day-number': { color: theme.palette.text.primary, fontWeight: 500 },

    /* Events */
    '& .fc-event': { border: 'none', padding: 0, background: 'transparent', cursor: 'pointer' },

    /* Theme vars */
    '& .fc-theme-standard': {
      '--fc-border-color': `${theme.palette.text.alt} !important`,
      '--fc-page-bg-color': `${theme.palette.background.alt} !important`,
      '--fc-today-bg-color': `${
        theme.palette.mode === 'dark'
          ? theme.palette.secondary.darker
          : theme.palette.secondary.light
      } !important`,
    },

    /* ===== Mobile layout (< sm) ===== */
    [theme.breakpoints.down('sm')]: {
      /* Wrap toolbar, create two rows */
      '& .fc-toolbar': {
        flexWrap: 'wrap',
        gap: 3,
      },

      /* First line: left chunk (prev/next/today) + center chunk (title) on one row */
      '& .fc-toolbar-chunk:first-of-type': {
        order: 1,
        alignItems: 'center',
        flex: '1 1 auto',
        gap: 6,
      },
      '& .fc-toolbar-chunk:nth-of-type(2)': {
        order: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        minWidth: 0,
      },

      '& .fc-toolbar-chunk:first-of-type .fc-button.fc-today-button': {
        padding: '4px 24px',
        fontSize: '1.05rem',
      },

      '& .fc-toolbar-title': {
        fontSize: { xs: '1.2rem', sm: '2rem', md: '2rem' }, // responsive sizes
        fontWeight: 600,
        lineHeight: 1.2,
        marginLeft: '8px',  // optional to separate from "today"
      },


      /* Second line: right chunk full-width, buttons spread and equal width */
      '& .fc-toolbar-chunk:last-of-type': {
        order: 2,
        flexBasis: '100%',
        display: 'flex',
        justifyContent: 'space-between',
      },
      /* Make the button group fill the row */
      '& .fc-toolbar-chunk:last-of-type .fc-button-group': {
        width: '100%',
        display: 'flex',
        gap: 0,
      },
      /* Make each view button flex equally */
      '& .fc-toolbar-chunk:last-of-type .fc-button-group .fc-button': {
        flex: 1,
        minWidth: 0,
        padding: '6px 0',
        fontSize: '0.9rem',
      },

      /* Tighten nav buttons + today a bit */
      '& .fc-toolbar .fc-button': {
        padding: '4px 10px',
      },
    },
  }}
>
      {/* Legend */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          mb: 2,
          mt: 1,
        }}
      >
        <Typography variant="h6" sx={{ mr: 1 }}>
          Legend:
        </Typography>
        {Object.entries(taskEmojiMap).map(([type, emoji]) => {
          if (!taskColorMap[type]) return null;
          const label = type.charAt(0).toUpperCase() + type.slice(1);
          const mappedColor = taskColorMap[type];
          const legendBgColor = type === 'blackout'
            ? (theme.palette[mappedColor]?.light || theme.palette[mappedColor]?.main || theme.palette.grey[700])
            : (theme.palette[mappedColor]?.dark || theme.palette[mappedColor]?.main || theme.palette.grey[700]);
          return (
            <Chip
              key={type}
              size="small"
              label={
                <>
                  <span style={{ marginRight: 4 }}>{emoji}</span>
                  {label}
                </>
              }
              variant="filled"
              sx={{
                backgroundColor: legendBgColor,
                color: theme.palette.getContrastText(legendBgColor),
              }}
            />
          );
        })}
      </Box>

      <FullCalendar
        plugins={[dayGridPlugin]}
        initialView="dayGridMonth"
        firstDay={calendarStart}
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,dayGridWeek,dayGridDay',
        }}
        events={events}
        eventContent={renderEventContent}
        eventClick={handleEventClick}
        slotEventOverlap={false}
        eventDisplay="block"
        height="auto"
      />
    </Box>
  );
};

export default TaskCalendar;



